import React, { useEffect, useReducer, useState, useMemo, type FC } from "react"
import ReactDOM from "react-dom"
import { Router, useLocation } from "react-router-dom"
import * as ReactRouter from "react-router-dom"
import { createBrowserHistory } from "history"
import { Global, css } from "@emotion/react"
import { PathReporter } from "io-ts/lib/PathReporter"

import "design-kit/src/assets/base-styles/Fonts.css"

import { type Token } from "./types"
import { TokenContext } from "./token"

import { getMyAccount, AuthError, newRequestId } from "./api"
import { initialAppState, appStateReducer, AppStateContext } from "./appState"
import { Success, Loading, Failure } from "@heyhabito/remote-data"
import * as HabitoDashboardEmbedUtils from "@heyhabito/dashboard-embed-utils"
import * as intercom from "./intercom"
import { SentryContext, initSentry } from "@heyhabito/sentry"
import { ChunkLoadErrorBoundary } from "./errorBoundaries"

import { breakpoints, colours, ModalContainer } from "design-kit"
import PageLoader from "./components/PageLoader"
import { SkipLink } from "./components/SkipLink"

import { alwaysInclude } from "@heyhabito/head-matter"

// Although we definitely need these resources, we load them dynamically so that
// they can be fetched in parallel network requests. These reduces the core
// bundle size, speeds up the initial render, and gets us to the all-important
// /my-account call faster

// Note we can't say `webpackChunkName: 'Main'` here because that name conflicts
// (case-insensitively) with the name `main` that Webpack gives to the top-level
// bundle, which causes the dev server to crash
const Main = React.lazy(
  () =>
    import(
      /*webpackChunkName: 'MainComponent', webpackPrefetch: true*/ "./Main"
    )
)

// The dynamically fetched apps expect these libs to be available globally (so
// that we can share the same instances, and to reduce bundle sizes)
interface WindowWithLibs extends Window {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  React: any
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  ReactDOM: any
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  ReactRouter: any
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  HabitoDashboardEmbedUtils: any
}
declare const window: WindowWithLibs

window.React = React
window.ReactDOM = ReactDOM
window.ReactRouter = ReactRouter
window.HabitoDashboardEmbedUtils = HabitoDashboardEmbedUtils

// NOTE: We can't store this instance on `window` or pass it to the embedded
// apps. Each app needs to initialise its own instance using its own Sentry DSN.
// ^ https://github.com/getsentry/sentry-javascript/issues/1656
const sentryContext = initSentry({
  dsn: __SENTRY_DSN__,
  isProd: __PRODUCTION__,
})

const browserHistory = createBrowserHistory()
browserHistory.listen(() => {
  intercom.refresh()
})

const redirectToLogin = (destination: string): void => {
  window.location.href =
    "/log-in?destination=" + encodeURIComponent(destination)
}

const GlobalStyles: FC<{ isDropdownOpen: boolean }> = ({ isDropdownOpen }) => {
  return (
    <Global
      styles={css`
        * {
          box-sizing: border-box;
        }

        html,
        body,
        #app {
          height: 100%;
          padding: 0;
          margin: 0;
          background-color: ${colours.greyScale.grey25};
        }

        body {
          ${isDropdownOpen ? `overflow: hidden;` : ""}

          ${breakpoints.desktop`
            overflow: auto;
          `}
        }
      `}
    />
  )
}

const ScrollToTop: FC = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

const App: FC = () => {
  const [token, setToken] = useState<Token | null>(null)
  const [state, dispatch] = useReducer(appStateReducer, initialAppState)

  const [dropdownOpen, setDropdownOpen] = useState(false)

  useEffect(() => {
    const go = async (): Promise<void> => {
      alwaysInclude(__GTM_APP_ID__)

      const maybeToken = window.localStorage.getItem("habito/token")

      if (!maybeToken) {
        // If there's no token the user must be signed out
        const destination = window.location.pathname
        redirectToLogin(destination)
        return
      } else {
        setToken(maybeToken)
      }

      dispatch({
        type: "setApplicationsRequest",
        applicationsRequest: Loading,
      })

      let myAccount = null
      const myAccountRequestId = newRequestId()
      try {
        const myAccountResult = await getMyAccount(
          maybeToken,
          myAccountRequestId
        )

        if (myAccountResult._tag === "Left") {
          // Our validation is probably wrong
          sentryContext.logToSentryDetailed({
            description: PathReporter.report(myAccountResult).join("; "),
            info: {
              cause: new Error("invalid response from my-account"),
            },
            // NOTE: tags can be used for searching/filtering
            tags: {
              "request-id": myAccountRequestId,
            },
          })
        } else {
          myAccount = myAccountResult.right
        }
      } catch (e) {
        if (e instanceof AuthError) {
          // The user could be logged out, or their token could have expired
          const destination = window.location.pathname
          redirectToLogin(destination)
        } else {
          const err = e instanceof Error ? e : new Error(`${e}`)
          sentryContext.logToSentryDetailed({
            description: "unexpected error while calling my-account",
            info: {
              cause: err,
              "request-id": myAccountRequestId,
            },
          })
        }
      }

      if (myAccount) {
        const { account, applications } = myAccount

        dispatch({
          type: "setAccount",
          account,
        })

        const applicationsMap = applications.reduce(
          (acc, app) => ({ ...acc, [app.externalApplicationId]: app }),
          {}
        )
        dispatch({
          type: "setApplicationsRequest",
          applicationsRequest: Success(applicationsMap),
        })

        const { accountId, email, intercomUserJwt } = account

        intercom.boot(accountId, email, intercomUserJwt)
      } else {
        dispatch({
          type: "setApplicationsRequest",
          applicationsRequest: Failure(new Error("Failed to fetch my-account")),
        })
      }
    }

    go()
  }, []) // Run once on mount

  const appState = useMemo(() => ({ state, dispatch }), [state, dispatch])

  return (
    <ChunkLoadErrorBoundary>
      <React.Suspense fallback={<PageLoader />}>
        <SentryContext.Provider value={sentryContext}>
          <AppStateContext.Provider value={appState}>
            <TokenContext.Provider value={token}>
              <Router history={browserHistory}>
                <ScrollToTop />
                <GlobalStyles isDropdownOpen={dropdownOpen} />
                <SkipLink />
                <Main
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                />
              </Router>
              <ModalContainer />
            </TokenContext.Provider>
          </AppStateContext.Provider>
        </SentryContext.Provider>
      </React.Suspense>
    </ChunkLoadErrorBoundary>
  )
}

ReactDOM.render(<App />, document.getElementById("app"))
